
import { logger } from '@/utils/logger';
import { createOpenAILogError } from '@/utils/loggerTypes';

/**
 * Tests the connection to the OpenAI API
 * @param apiKey The OpenAI API key to test
 * @param model Optional model to test, defaults to gpt-3.5-turbo
 * @returns A promise that resolves to a result object
 */
export async function testOpenAIConnection(
  apiKey: string,
  model: string = 'gpt-3.5-turbo'
): Promise<{
  success: boolean;
  response?: string;
  error?: string;
}> {
  console.log(`Testing OpenAI connection with model: ${model}`);
  
  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: model,
        messages: [
          {
            role: 'system',
            content: 'You are a helpful assistant.'
          },
          {
            role: 'user',
            content: 'This is a test message to verify the API connection is working. Please respond with "Connection successful!" and nothing more.'
          }
        ],
        temperature: 0.7,
        max_tokens: 50
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`API responded with status ${response.status}: ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    const content = data.choices[0]?.message?.content.trim();
    
    logger.info('OpenAI API connection test successful', {
      component: 'testConnection',
      context: {
        model: model
      }
    });
    
    return {
      success: true,
      response: content
    };
  } catch (error) {
    // Log the error with proper type
    logger.error('OpenAI API connection test failed', 
      createOpenAILogError((error as Error).message, {
        operation: 'chat',  // Changed from "test" to "chat" to fix type error
        model: model,
        component: 'testConnection'
      })
    );
    
    return {
      success: false,
      error: (error as Error).message
    };
  }
}

// Make the function available globally for interactive testing
if (typeof window !== 'undefined') {
  (window as any).testOpenAIConnection = testOpenAIConnection;
}
